import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getTenantConfig } from 'utils/get-tenant-config';
import { isValidTenantId } from 'utils/is-valid-tenant-id';
import { initFeatureFlagsWithTenantId } from '@web-config-app/core';
import { SESSION_STORAGE_LOGOUT_INITIATED } from '../../constants/storage-key';
import { resolveTenantIdAlias } from '../../utils/resolve-tenant-id-alias';
import { useTenantId } from '../use-tenant-id/use-tenant-id';
import { discoveryConfig } from '../../configs/discovery-config';

const TENANT_URL_PATTERN = /^\/tenants\/([^/]+)/;

function parseTenantIdFromUrl(pathname: string): string {
  const match = pathname.match(TENANT_URL_PATTERN);
  return match?.[1] ?? '';
}

/**
 * Resolves the active tenant from the URL, session storage, and logout state.
 *
 * Handles:
 * - Parsing `tenantId` from `/tenants/:tenantId/…` URLs (including legacy
 *   aliases, e.g. `b2b` → `league`)
 * - Falling back to session storage when the URL has no tenant segment
 * - Suppressing the tenant during logout (via sessionStorage flag) so
 *   `LeagueProvider` is never mounted for the old tenant
 * - Syncing URL → session storage when the user navigates to a new tenant
 * - Treating unrecognized URL tenants as "no tenant" (shows the tenant
 *   selection form) and clearing any stale stored value
 * - Clearing the logout-initiated session flag once a valid tenant route
 *   is reached
 * - Initializing feature flags for the resolved tenant
 *
 * When no tenant has been resolved yet, `activeConfig` falls back to
 * `discoveryConfig` so Auth0 domain discovery can authenticate the user.
 * Once `RootHandler` sets the tenant from the discovery token, this hook
 * resolves the real tenant config and `LeagueProvider` updates the global
 * config store via `setConfig`.
 *
 * @returns `tenantId` — the resolved tenant ID (empty string if none)
 * @returns `setTenantId` — setter to update the stored tenant ID
 * @returns `config` — tenant config from `getTenantConfig`, or `undefined`
 * @returns `activeConfig` — `config`, or `discoveryConfig` when no tenant is
 *   resolved yet
 */
export const useResolvedTenant = () => {
  const location = useLocation();
  const { tenantId: storedTenantId, setTenantId } = useTenantId();
  const previousUrlTenantIdRef = useRef<string | null>(null);

  const urlTenantId = resolveTenantIdAlias(
    parseTenantIdFromUrl(location.pathname),
  );
  const hasValidUrlTenantId = isValidTenantId(urlTenantId);
  const hasValidStoredTenantId = isValidTenantId(storedTenantId);

  // After logout Auth0 returns to `/` while the tenant is still in
  // session storage. Detect the flag synchronously so we never mount
  // LeagueProvider (and its Auth0 SDK) for the old tenant.
  const logoutInitiated = sessionStorage.getItem(
    SESSION_STORAGE_LOGOUT_INITIATED,
  );
  let resolvedTenantId = '';
  if (hasValidUrlTenantId) {
    resolvedTenantId = urlTenantId;
  } else if (!urlTenantId && hasValidStoredTenantId) {
    resolvedTenantId = storedTenantId;
  }
  const tenantId = logoutInitiated ? '' : resolvedTenantId;

  useEffect(() => {
    if (logoutInitiated && storedTenantId) {
      setTenantId('');
    }
  }, [logoutInitiated, storedTenantId, setTenantId]);

  useEffect(() => {
    const urlTenantChanged = previousUrlTenantIdRef.current !== urlTenantId;
    previousUrlTenantIdRef.current = urlTenantId;

    // Only persist the URL tenant when this tab's URL actually changes.
    // If another tab writes a different tenant elsewhere, this tab
    // still owns its URL and should not "fight back" by rewriting storage.
    if (
      urlTenantChanged &&
      urlTenantId &&
      urlTenantId !== storedTenantId &&
      hasValidUrlTenantId
    ) {
      setTenantId(urlTenantId);
    }
    if (
      hasValidUrlTenantId &&
      sessionStorage.getItem(SESSION_STORAGE_LOGOUT_INITIATED)
    ) {
      sessionStorage.removeItem(SESSION_STORAGE_LOGOUT_INITIATED);
    }
  }, [urlTenantId, storedTenantId, hasValidUrlTenantId, setTenantId]);

  // If the URL has an unrecognized tenant, clear any stale stored value
  // so the tenant-selection form is shown.
  useEffect(() => {
    if (urlTenantId && !hasValidUrlTenantId && storedTenantId) {
      setTenantId('');
    }
  }, [urlTenantId, hasValidUrlTenantId, storedTenantId, setTenantId]);

  const config = useMemo(() => getTenantConfig(tenantId), [tenantId]);

  // Fall back to discoveryConfig before a tenant is selected so LeagueProvider
  // can mount with Auth0 domain-discovery auth and return the tenant_id claim.
  // Once RootHandler sets the tenant, this hook resolves the real config and
  // LeagueProvider updates the global config store via its useEffect on the
  // config prop change.
  const activeConfig = config ?? discoveryConfig;

  useEffect(() => {
    initFeatureFlagsWithTenantId(tenantId);
  }, [tenantId]);

  return { tenantId, setTenantId, config, activeConfig };
};
